import type {
  McpClientId,
  McpClientSetup,
  McpSetupArtifact,
} from './client-types'

export type {
  McpClientId,
  McpClientSetup,
  McpSetupArtifact,
  McpSetupRecipe,
} from './client-types'

export const MCP_SETUP_SUMMARY =
  'ChatGPT web uses a Brew plugin. ChatGPT desktop and Codex share MCP server settings. In other clients, follow the setup below, authenticate, then call get_brew_capabilities.'
export const MCP_HANDOFF_SETUP_HINT =
  'In ChatGPT web, install or enable the Brew plugin. In ChatGPT desktop or Codex, add Brew as a Streamable HTTP MCP server and authenticate. Other clients use their own MCP connection settings.'
export const MCP_VERIFY =
  'Call get_brew_capabilities to confirm the connection and inspect its brand or organization scope.'
export const MCP_GENERIC_CAVEAT =
  'This example is for clients that accept mcpServers and type: http. VS Code uses servers, OpenCode uses mcp and type: remote, and Cline requires type: streamableHttp. Follow your client’s recipe.'

const urlArtifact: McpSetupArtifact = {
  kind: 'url',
  language: 'text',
  build: (url) => url,
}
const json = (obj: unknown): string => JSON.stringify(obj, null, 2)
const config = (
  filename: string,
  build: (url: string) => unknown
): McpSetupArtifact => ({
  kind: 'config',
  filename,
  language: 'json',
  build: (url) => json(build(url)),
})
// Configuration URLs are copied into a shell, so shell metacharacters stay literal.
const shellQuote = (value: string): string =>
  "'" + value.replaceAll("'", `'"'"'`) + "'"

export function buildMcpApiKeyConfig(url: string): string {
  return json({
    mcpServers: {
      brew: {
        type: 'http',
        url,
        headers: { Authorization: 'Bearer brew_YOUR_API_KEY' },
      },
    },
  })
}

export function buildMcpRemoteApiKeyConfig(url: string): string {
  return json({
    mcpServers: {
      brew: {
        command: 'npx',
        args: [
          '-y',
          'mcp-remote',
          url,
          '--header',
          // biome-ignore lint/suspicious/noTemplateCurlyInString: expanded by mcp-remote from the client environment
          'Authorization:${AUTH_HEADER}',
        ],
        env: { AUTH_HEADER: 'Bearer brew_YOUR_API_KEY' },
      },
    },
  })
}

export const MCP_CLIENTS: ReadonlyArray<McpClientSetup> = [
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    logoDomain: 'claude.com',
    tagline: 'Claude Desktop, claude.ai and Cowork',
    authentication: 'oauth',
    sources: ['https://support.claude.com/en/articles/11175166'],
    recipes: [
      {
        title: 'Custom connector',
        steps: [
          'Open **Customize → Connectors → + Add → Add custom connector**.',
          'Name it Brew, enter the server URL below, and select **Continue**. Review the detected authentication settings and continue.',
        ],
        artifact: urlArtifact,
        authentication:
          'Choose **Sign in now** or connect when needed. Finish adding the connector, select **Connect**, sign in with Brew, and choose one brand or all brands.',
        verification:
          'Enable Brew from the conversation’s **+ → Connectors** menu. ' +
          MCP_VERIFY,
        note: 'For Team or Enterprise, an owner or authorized admin first uses **Organization settings → Connectors → Add → Custom → Web**. Members then connect individually. Free accounts are limited to one custom connector.',
      },
      {
        title: 'API-key alternative',
        steps: [
          'Use the same custom connector flow and server URL. Choose **No sign in** when using a fixed API key.',
        ],
        artifact: urlArtifact,
        authentication:
          'Under **Request headers**, add **Authorization** with the value **Bearer brew_YOUR_API_KEY**, using a key from Brew’s **Settings → API**. Finish adding the connector.',
        verification: MCP_VERIFY,
        note: 'On Team or Enterprise, a fixed credential is shared by everyone using the connector. Choose a key limited to the access those members need.',
      },
    ],
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT web',
    logoDomain: 'openai.com',
    tagline: 'chatgpt.com uses plugins',
    authentication: 'oauth',
    sources: [
      'https://help.openai.com/en/articles/12584461-connecting-custom-mcp-servers-in-chatgpt',
    ],
    recipes: [
      {
        title: 'Web plugin',
        steps: [
          'On chatgpt.com, open **Plugins → + → Add custom MCP server**.',
          'Name it Brew. Under **Connection**, enter the server URL below and choose **OAuth**.',
          'Review the warning, select **I understand and want to continue**, then **Create as a plugin**.',
        ],
        artifact: urlArtifact,
        authentication:
          'Install the plugin. Complete the OAuth sign-in with Brew, choose one brand or all brands, and approve the access when prompted.',
        verification:
          'In a new chat, type **@** and select **Brew**. ' + MCP_VERIFY,
        note: 'Full MCP support on the web requires Business or Enterprise/Edu. Pro supports read/fetch access. Workspace permissions govern who can create custom MCP plugins; Business requires an admin or owner.',
      },
    ],
    caveat:
      'If tools changed after installation, refresh the plugin’s tools or actions using the controls your workspace provides. An administrator may need to review and publish the update.',
  },
  {
    id: 'grok',
    name: 'Grok',
    logoDomain: 'x.ai',
    tagline: 'Grok web and Grok Build',
    authentication: 'oauth',
    sources: [
      'https://docs.x.ai/grok/connectors',
      'https://docs.x.ai/build/features/mcp-servers',
    ],
    recipes: [
      {
        title: 'Web connector',
        steps: [
          'Open **grok.com/connectors → New Connector → Custom** and enter the server URL below.',
        ],
        artifact: urlArtifact,
        authentication:
          'Start authentication, sign in with Brew, select a brand or all brands, and approve access.',
        verification:
          'Start a new chat; Grok can use connected tools when relevant. ' +
          MCP_VERIFY,
        note: 'For Business or Enterprise, a team admin provisions the connector first.',
      },
      {
        title: 'Grok Build',
        steps: ['Run the command below.'],
        artifact: {
          kind: 'command',
          language: 'bash',
          build: (url) =>
            `grok mcp add --transport http brew ${shellQuote(url)}`,
        },
        authentication:
          'If authentication is needed, open **/mcps**, select Brew, and press **i** to sign in with Brew and select your brand.',
        verification:
          'Run `grok mcp doctor brew`, then start a session. ' + MCP_VERIFY,
      },
    ],
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    logoDomain: 'claude.com',
    tagline: 'Add configuration, then authenticate',
    authentication: 'oauth',
    sources: ['https://code.claude.com/docs/en/mcp'],
    recipes: [
      {
        title: 'Command line',
        steps: [
          'Run the command below with **--scope user** for every project, or omit it for this project’s local configuration. Adding the server only saves its configuration.',
        ],
        artifact: {
          kind: 'command',
          language: 'bash',
          build: (url) =>
            `claude mcp add --transport http --scope user brew ${shellQuote(url)}`,
        },
        authentication:
          'Run `claude mcp login brew`, or open **/mcp** in a Claude Code session and select Brew to authenticate. Sign in with Brew and choose a brand or all brands.',
        verification:
          'Run **/mcp** to confirm Brew is connected. ' + MCP_VERIFY,
      },
    ],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    logoDomain: 'cursor.com',
    tagline: 'Customize → MCP, or a config file',
    authentication: 'oauth',
    paths: { unix: '~/.cursor/mcp.json', project: '.cursor/mcp.json' },
    sources: [
      'https://cursor.com/docs/mcp',
      'https://cursor.com/docs/context/mcp/install-links',
    ],
    recipes: [
      {
        title: 'Install or configure',
        steps: [
          'Use **Add to Cursor** below, or merge the configuration into **~/.cursor/mcp.json** (all projects) or **.cursor/mcp.json** (this project).',
        ],
        artifact: config('mcp.json', (url) => ({
          mcpServers: { brew: { url } },
        })),
        authentication:
          'Open **Customize → MCP**, select Brew, and use its sign-in action if prompted. Sign in with Brew and choose a brand or all brands.',
        verification: 'Confirm Brew’s tools appear in Customize. ' + MCP_VERIFY,
        note: 'Older Cursor builds call this settings section **Tools & MCP**.',
      },
    ],
  },
  {
    id: 'opencode',
    name: 'OpenCode',
    logoDomain: 'opencode.ai',
    tagline: 'Add to ~/.config/opencode/opencode.json',
    authentication: 'oauth',
    paths: { unix: '~/.config/opencode/opencode.json' },
    sources: ['https://opencode.ai/docs/mcp-servers/'],
    recipes: [
      {
        title: 'Config file',
        steps: ['Merge this block into **~/.config/opencode/opencode.json**.'],
        artifact: config('opencode.json', (url) => ({
          mcp: { brew: { type: 'remote', url, enabled: true } },
        })),
        authentication:
          'Run `opencode mcp auth brew`, sign in with Brew, and select a brand or all brands. OpenCode can also prompt on first use.',
        verification:
          'Run `opencode mcp list` to confirm Brew is connected. ' + MCP_VERIFY,
      },
    ],
  },
  {
    id: 'vscode',
    name: 'VS Code',
    logoDomain: 'code.visualstudio.com',
    tagline: 'GitHub Copilot and MCP: Add Server',
    authentication: 'oauth',
    paths: {
      unix: 'MCP: Open User Configuration',
      project: '.vscode/mcp.json',
    },
    sources: [
      'https://code.visualstudio.com/docs/agent-customization/mcp-servers',
      'https://docs.github.com/en/copilot/how-tos/provide-context/use-mcp-in-your-ide/extend-copilot-chat-with-mcp',
    ],
    recipes: [
      {
        title: 'Workspace or user configuration',
        steps: [
          'Run **MCP: Add Server** from the Command Palette and choose HTTP, or create **.vscode/mcp.json** with the block below.',
          'For all workspaces, use **MCP: Open User Configuration**.',
        ],
        artifact: config('mcp.json', (url) => ({
          servers: { brew: { type: 'http', url } },
        })),
        authentication:
          'Select **Start** above Brew’s entry in mcp.json. Complete the browser sign-in with Brew and select a brand or all brands.',
        verification:
          'In Chat, select **Agent**, then **Configure Tools** and enable Brew’s tools. ' +
          MCP_VERIFY,
        note: 'Requires GitHub Copilot. On Copilot Business or Enterprise, an administrator must allow MCP servers in Copilot.',
      },
    ],
  },
  {
    id: 'openai-codex',
    name: 'Codex and ChatGPT desktop',
    logoDomain: 'openai.com',
    tagline: 'One configuration for desktop, CLI and IDE on the same host',
    authentication: 'oauth',
    paths: {
      unix: '~/.codex/config.toml',
      project: '.codex/config.toml (trusted projects)',
    },
    sources: ['https://learn.chatgpt.com/docs/extend/mcp'],
    recipes: [
      {
        title: 'ChatGPT desktop',
        steps: [
          'Open **Settings → Plugins → MCPs → Add → Add MCP server**. Some builds use **Settings → MCP servers → Add server**.',
          'Name it brew, select **Streamable HTTP**, and enter the URL below. Leave token and header fields empty for OAuth, then **Save**. Restart the connection if offered.',
        ],
        artifact: urlArtifact,
        authentication:
          'Select **Authenticate** on the Brew server, sign in with Brew, and choose a brand or all brands.',
        verification:
          'Type **/mcp** to inspect connected servers. ' + MCP_VERIFY,
      },
      {
        title: 'Codex CLI or IDE configuration',
        steps: [
          'Merge this block into **~/.codex/config.toml**. The desktop app, CLI and IDE extension share it on the same Codex host.',
        ],
        artifact: {
          kind: 'config',
          filename: 'config.toml',
          language: 'toml',
          build: (url) => `[mcp_servers.brew]\nurl = ${JSON.stringify(url)}`,
        },
        authentication:
          'Run `codex mcp login brew`, sign in with Brew, and select a brand or all brands. The desktop and IDE server lists also offer **Authenticate**.',
        verification:
          'Run `codex mcp list` or **/mcp** in a session. ' + MCP_VERIFY,
        note: 'Windows and WSL can use different hosts and home directories. ChatGPT web does not read this file. For an API key, set bearer_token_env_var to the name of an environment variable containing your Brew key.',
      },
    ],
  },
  {
    id: 'manus',
    name: 'Manus',
    logoDomain: 'manus.im',
    tagline: 'Custom MCP server with an API key',
    authentication: 'api-key',
    sources: [
      'https://manus.im/features/connectors',
      'https://manus.im/docs/integrations/custom-mcp',
    ],
    recipes: [
      {
        title: 'Custom connector',
        steps: [
          'Open **Connectors → Create → Add MCP by URL**. Name the server Brew and enter the URL below. Older builds use **Settings → Integrations → Custom MCP Servers → Add Server**.',
        ],
        artifact: urlArtifact,
        authentication:
          'Create a key in Brew’s **Settings → API**. Use the key in Manus’s bearer-token field, or set **Authorization** to **Bearer brew_YOUR_API_KEY**. Save and test the connection.',
        verification: 'Confirm Brew’s tools are available. ' + MCP_VERIFY,
        note: 'Use API-key authentication for custom servers. Brew OAuth in Manus’s custom-server flow still needs a live compatibility check.',
      },
    ],
  },
  {
    id: 'windsurf',
    name: 'Devin Desktop',
    logoDomain: 'devin.ai',
    tagline: 'Formerly Windsurf; configure Devin Local',
    authentication: 'oauth',
    paths: {
      unix: '~/.config/devin/mcp_config.json',
      windows: String.raw`%APPDATA%\devin\mcp_config.json`,
      project: '.devin/mcp_config.local.json',
    },
    sources: [
      'https://docs.devin.ai/cli/extensibility/mcp/configuration',
      'https://docs.devin.ai/desktop/changelog',
    ],
    recipes: [
      {
        title: 'Devin Local configuration',
        steps: [
          String.raw`Merge this block into **~/.config/devin/mcp_config.json** on macOS/Linux, or **%APPDATA%\devin\mcp_config.json** on Windows. For private project settings, use **.devin/mcp_config.local.json**.`,
        ],
        artifact: config('mcp_config.json', (url) => ({
          mcpServers: { brew: { url } },
        })),
        authentication:
          'Run `devin mcp login brew` in Devin CLI, or use the authentication action when Devin Local first uses Brew. Sign in with Brew and select a brand or all brands.',
        verification:
          'Run `devin mcp list` or inspect the session’s MCP servers. ' +
          MCP_VERIFY,
        note: 'Current Devin Desktop uses Devin Local. Upgrade legacy Windsurf builds before following these steps. Organization policy may restrict available servers.',
      },
    ],
  },
  {
    id: 'cline',
    name: 'Cline',
    logoDomain: 'cline.bot',
    tagline: 'Remote Servers with Streamable HTTP',
    authentication: 'oauth',
    sources: ['https://docs.cline.bot/mcp/mcp-overview'],
    recipes: [
      {
        title: 'Remote Servers',
        steps: [
          'Click the **MCP Servers** icon and open **Remote Servers**.',
          'Enter Server Name brew and the Server URL below. Choose **Streamable HTTP**, then **Add Server**.',
        ],
        artifact: urlArtifact,
        authentication:
          'Click **Authenticate** on the Brew server row. Complete the browser sign-in with Brew and select a brand or all brands.',
        verification: MCP_VERIFY,
      },
      {
        title: 'JSON alternative',
        steps: [
          'Open **MCP Servers → Configure → Configure MCP Servers** and merge the block below into the settings file. Save it.',
        ],
        artifact: config('cline_mcp_settings.json', (url) => ({
          mcpServers: {
            brew: { type: 'streamableHttp', url, disabled: false },
          },
        })),
        authentication:
          'Click **Authenticate** on Brew’s server row, sign in with Brew, and select a brand or all brands.',
        verification: MCP_VERIFY,
        note: 'The type field is required here: omitting it selects legacy SSE in Cline.',
      },
    ],
  },
  {
    id: 'goose',
    name: 'Goose',
    logoDomain: 'goose-docs.ai',
    tagline: 'Streamable HTTP extension',
    authentication: 'oauth',
    sources: ['https://goose-docs.ai/docs/getting-started/using-extensions/'],
    recipes: [
      {
        title: 'Goose CLI',
        steps: [
          'Run goose configure. Choose **Add Extension → Remote Extension (Streamable HTTP)**, name it brew, and enter the endpoint below.',
        ],
        artifact: {
          kind: 'command',
          language: 'bash',
          build: (url) =>
            `goose configure\n# Add Extension → Remote Extension (Streamable HTTP)\n# name: brew\n# endpoint: ${url}`,
        },
        authentication:
          'Complete the OAuth sign-in when Goose requests it. Sign in with Brew and select a brand or all brands.',
        verification:
          'Start a session with the Brew extension enabled. ' + MCP_VERIFY,
        note: 'Goose Desktop: open **Extensions → Add custom extension**, choose **Streamable HTTP**, and enter the same endpoint.',
      },
    ],
  },
  {
    id: 'generic',
    name: 'Any MCP client',
    logoDomain: 'modelcontextprotocol.io',
    tagline: 'Use your client’s Streamable HTTP format',
    authentication: 'oauth',
    sources: [
      'https://modelcontextprotocol.io/specification/latest/basic/transports',
    ],
    recipes: [
      {
        title: 'HTTP JSON example',
        steps: [
          'Use your client’s documented remote-server format. The example below is for clients that accept mcpServers with type http. Merge it into your existing config.',
        ],
        artifact: config('mcp.json', (url) => ({
          mcpServers: { brew: { type: 'http', url } },
        })),
        authentication:
          'Run the client’s sign-in or authenticate action, then sign in with Brew and choose a brand or all brands. For clients without OAuth, use an API-key bearer header in their supported format.',
        verification: MCP_VERIFY,
        note: MCP_GENERIC_CAVEAT,
      },
    ],
  },
]

export function getMcpClient(id: McpClientId): McpClientSetup {
  const client = MCP_CLIENTS.find((candidate) => candidate.id === id)
  if (!client) {
    throw new Error(`Unknown MCP client: ${id}`)
  }
  return client
}
